/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2021-2025 (original work) Open Assessment Technologies SA
 */

import { writable, get } from 'svelte/store';
import { showNotification } from '@oat-sa-private/ui-components';
import dynamicKeys from '@/locale/dynamicKeys';
import { __ } from '@oat-sa-private/ui-core';
import getInteractionUserName from '@/core/utils/getInteractionUserName';
import { actionsList, routes } from '@/core/utils/consts';
import { retryingAcs as retryingAcsStore } from '@/store/retryingAcs';
import router from '@/core/router/router';
import actionToRouteKey from '@/core/utils/actionToRouteKey';

// stores requested Acs with no returned result yet
export const onGoingAcs = writable([]);

// stores requested Acs with returned successful result, these are removed on notification close
export const resolvedAcs = writable([]);

// stores requested Acs with returned unsuccessful result, these are removed on retry attempt
export const rejectedAcs = writable([]);

/**
 * Adds ACS to onGoingAcs array
 * @param {Array} data - Array of ACS
 */
export function add(data) {
    onGoingAcs.update(storeValue => {
        storeValue.push(...data);
        return storeValue;
    });
}

/**
 * Removes each resolved ACS from onGoingAcs and
 * adds it to resolvedAcs
 * @param {Object} data - Object containing each resolved ACS. Key of each Acs is dxId of attached interaction
 */
export function resolve(data) {
    updateSettledAcs(data);
    clearOnGoingAcs(data);
}

/**
 * Maps collection of successful and failed ACS and push them into resolvedAcs store
 * @param {Object} data - Object containing each resolved ACS. Key of each Acs is dxId of attached interaction
 */
function updateSettledAcs(data) {
    const resolved = [];
    const rejected = [];
    const currentAcs = get(onGoingAcs);

    for (const acs in data) {
        data[acs].success
            ? resolved.push(currentAcs.find(item => item.delivery_execution_id === acs))
            : rejected.push(currentAcs.find(item => item.delivery_execution_id === acs));
    }

    if (resolved.length > 0) {
        resolvedAcs.update(storeValue => [...storeValue, ...resolved]);
        buildAcsNotification(resolved, true);
    }

    if (rejected.length > 0) {
        rejectedAcs.update(storeValue => [...storeValue, ...rejected]);
        buildAcsNotification(rejected, false);
    }
}

/**
 *  Removes resolved ACS from onGoingAcs store
 * @param {Object} data - Object containing each resolved ACS. Key of each Acs is dxId of attached interaction
 */
function clearOnGoingAcs(data) {
    onGoingAcs.update(storeValue => {
        storeValue = storeValue.filter(storedAcs => typeof data[storedAcs.delivery_execution_id] === 'undefined');
        return storeValue;
    });
}

/**
 * Builds notification config for current ACS request resolution
 * @param {Array} acsData - Object containing each resolved ACS. Key of each Acs is dxId of attached interaction
 * @param {Boolean} success - If ACS request succeeded or failed
 */
export function buildAcsNotification(acsData, success) {
    const persistence = success ? 'autoclose' : 'persistent';
    const closeButtonSuccess = {
        icon: 'remove-16',
        size: 'base-16',
        label: __('Close'),
        ariaLabel: __('Close')
    };
    const closeButtonFailure = {
        icon: 'arrow-right-16',
        size: 'base-16',
        label: __('Details'),
        ariaLabel: __('Details')
    };

    showNotification(
        {
            hierarchy: success ? 'success' : 'alert',
            closeable: true,
            closeButtonProps: success ? closeButtonSuccess : closeButtonFailure,
            title: buildAcsNotificationMessage(
                acsData.length,
                acsData[0].userName,
                acsData[0].sub,
                acsData[0].action,
                success
            ),
            /* eslint-disable indent */
            data: {
                acsData,
                closeCallback:
                    success === false
                        ? () => {
                              retryingAcsStore.set(acsData);
                              acsData[0].action = actionToRouteKey(acsData[0].action);
                              let action = `retry${acsData[0].action[0].toUpperCase() + acsData[0].action.slice(1)}`;
                              router.redirect(routes[action]);
                          }
                        : null
            }
            /* eslint-enable indent */
        },
        persistence
    );
}

/**
 * Builds notification message to be displayed for current ACS request resolution
 * @param {Number} length - Number of ACS included on resolved request
 * @param {String} userName - User name assigned to the dxId of the first resolved ACS
 * @param {String} sub - User id assigned to the dxId of the first resolved ACS
 * @param {String} action - ACS action of the first resolved ACS
 * @param {Boolean} success If ACS request succeeded or failed
 * @returns {String} - Notification message
 */
function buildAcsNotificationMessage(length, userName, sub, action, success) {
    /* eslint-disable indent */
    let res = '';
    if (action === actionsList.authorize) {
        res +=
            length > 1
                ? `${__('Authorizing test takers')} (${length}) `
                : `${__('The authorization of the test taker')} ${getInteractionUserName({
                      name: userName,
                      id: sub
                  })} `;
    } else if (action === actionsList.report) {
        res +=
            length > 1
                ? `${__('Reporting test takers')} (${length}) `
                : `${__('Report for test taker')} ${getInteractionUserName({
                      name: userName,
                      id: sub
                  })} `;
    } else if (action === actionsList.terminate) {
        res +=
            length > 1
                ? `${__('Terminating tests')} (${length}) `
                : `${__('The termination of the test taker')} ${getInteractionUserName({
                      name: userName,
                      id: sub
                  })} `;
    } else if (action === actionsList.reopen) {
        res +=
            length > 1
                ? `${__('Reopening tests')} (${length}) `
                : `${__('The reopening of the test taker')} ${getInteractionUserName({
                      name: userName,
                      id: sub
                  })} `;
    } else if (action === actionsList.pause) {
        res +=
            length > 1
                ? `${__('Pausing test takers')} (${length}) `
                : `${__('The pause of the test taker')} ${getInteractionUserName({
                      name: userName,
                      id: sub
                  })} `;
    } else if (action === 'extendTime') {
        res +=
            length > 1
                ? `${__('Additional time')} (${length}) `
                : `${__('The extend time of the test taker')} ${getInteractionUserName({
                      name: userName,
                      id: sub
                  })} `;
    } else if (action === 'resetExtraTime') {
        res +=
            length > 1
                ? `${__('Remove additional time')} (${length}) `
                : `${__('Remove additional time of the test taker')} ${getInteractionUserName({
                      name: userName,
                      id: sub
                  })} `;
    } else if (action === actionsList.resume) {
        res +=
            length > 1
                ? `${__('Tests resumed')} (${length}) `
                : `${__('The resume of the test taker')} ${getInteractionUserName({
                      name: userName,
                      id: sub
                  })} `;
    } else if (action === actionsList.reset) {
        res +=
            length > 1
                ? `${__('Resetting sessions')} (${length}) `
                : `${__('Resetting session for test taker')} ${getInteractionUserName({
                      name: userName,
                      id: sub
                  })} `;
    }
    /* eslint-enable indent */

    res += dynamicKeys()[`acs-${length > 1 ? 'multiple' : 'single'}-${action}-${success ? 'success' : 'fail'}`];

    return res;
}
